import React, { useMemo } from 'react';

import { graphql, useFragment } from 'react-relay';

import { getProductAndIDFromARI } from '@atlassian/search-client/ari';
import { type SearchResultType as SearchClientSearchResultType } from '@atlassian/search-client/common/types';
import { getGenericUrlWithAnalyticsQueryParams } from '@atlassian/search-common/analytics-query-params';

import {
	ProductKeys,
	ProductKeys1PForAnalytics,
} from '../../../../../../common/constants/product-keys';
import type { SearchResultProps } from '../../../../../../common/types';
import { getAtlasEntityIcon } from '../../../../../../common/ui/product-entity-icon';
import { SearchResult } from '../../../../../../common/ui/search-result';
import { SearchResultType } from '../../../../../../common/ui/search-result-type';
import type { AdvancedSearchResultInteractedMetadata } from '../../../../../../common/utils/events/advanced-search-result-interacted';
import { enrichExcerpt } from '../../../../../../common/utils/search-result-utils';
import { getSiteMetadata } from '../../../../../../common/utils/sites';
import { ResultAnalytics } from '../../../../../../controllers/analytics/result-analytics';
import type { ResultDerivedDataProps } from '../../../result-derived-data';
import type { searchAtlasProject_AtlassianSearchPage$key } from './__generated__/searchAtlasProject_AtlassianSearchPage.graphql';
import { ProjectTitleMetadata } from './project-title-metadata';

export const SearchAtlasProject = ({
	preparedResultAttributes,
	intentDetectionTriggered,
	node,
	position,
	searchSessionId,
	primaryProduct,
	primarySubProduct,
	selectedProduct,
	appMode,
	mainContentWidth,
	selectedSites,
	productConfigs,
	cloudId,
}: SearchResultProps<searchAtlasProject_AtlassianSearchPage$key> &
	ResultDerivedDataProps): React.JSX.Element => {
	const data = useFragment(
		graphql`
			fragment searchAtlasProject_AtlassianSearchPage on SearchResultAtlasProject {
				...projectTitleMetadata_AtlassianSearchPage
				__typename
				id
				type
				title
				url
				iconUrl
				project {
					key
				}
			}
		`,
		node,
	);

	const metadata = useMemo(() => {
		const [product, , , contentId] = getProductAndIDFromARI(data.id);
		return {
			id: data.id,
			containerId: null,
			spaceId: null,
			contentType: data.type,
			entityType: data.type,
			contentId,
			product,
			...getSiteMetadata(data.id, cloudId, selectedSites),
		} satisfies AdvancedSearchResultInteractedMetadata;
	}, [data.id, data.type, cloudId, selectedSites]);

	const enrichedTitle = useMemo(() => {
		const prefix = data.project?.key;
		if (prefix) {
			return enrichExcerpt(prefix.concat('\u2002', data.title));
		}
		return enrichExcerpt(data.title);
	}, [data.title, data.project?.key]);

	const url = useMemo(() => {
		return getGenericUrlWithAnalyticsQueryParams({ url: data.url, searchSessionId });
	}, [data.url, searchSessionId]);

	const headerMetadataParts = [
		['type', <SearchResultType type={data.type as SearchClientSearchResultType} />],
	];

	const icon = getAtlasEntityIcon({
		productConfigs,
		iconUrl: data.iconUrl,
		type: data.type,
		size: 'medium',
	});

	return (
		<ResultAnalytics
			preparedResultAttributes={preparedResultAttributes}
			intentDetectionTriggered={intentDetectionTriggered}
			metadata={metadata}
			position={position}
		>
			{({
				onResultClicked,
				onResultContextMenu: onResultCtxMenuClicked,
				onCopyLinkButtonClicked,
				onSummarizeInChatButtonClicked,
				onContextMenuCopyButtonClicked,
				onContextMenuButtonClicked,
			}) => (
				<SearchResult
					productConfigs={productConfigs}
					{...data}
					product={ProductKeys1PForAnalytics.AtlasProjects}
					metadataProduct={ProductKeys.Atlas}
					productSelected={!!selectedProduct}
					primaryProduct={primaryProduct}
					primarySubProduct={primarySubProduct}
					onResultClicked={onResultClicked}
					onResultContextMenu={onResultCtxMenuClicked}
					onCopyLinkButtonClicked={onCopyLinkButtonClicked}
					onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
					onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
					onContextMenuButtonClicked={onContextMenuButtonClicked}
					url={url}
					icon={icon}
					headerMetadataParts={headerMetadataParts}
					titleMetadata={<ProjectTitleMetadata metadata={data} productConfigs={productConfigs} />}
					title={enrichedTitle}
					description={<></>} // no known blockers to adding description other than the API doesn't return description for projects
					position={position}
					partial={data}
					appMode={appMode}
					mainContentWidth={mainContentWidth}
					selectedSites={selectedSites}
				/>
			)}
		</ResultAnalytics>
	);
};

export const isSearchAtlasProject = (
	node?: any,
): node is searchAtlasProject_AtlassianSearchPage$key => {
	return node?.id?.includes('townsquare') && node?.__typename === 'SearchResultAtlasProject';
};
